import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/homphyApi';

type Preview = { appointmentId: number; providerName: string; sessionDate: string };
export default function SessionConfirmPage() {
  const { token = '' } = useParams();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, nofollow';
    let referrer = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
    if (!referrer) { referrer = document.createElement('meta'); referrer.name = 'referrer'; document.head.appendChild(referrer); }
    const previousReferrer = referrer.content;
    referrer.content = 'no-referrer';
    return () => { meta!.content = 'index, follow'; referrer!.content = previousReferrer; };
  }, []);
  useEffect(() => {
    if (!user || !token) return;
    api.post<Preview>('/appointments/session/confirmation/preview', { token })
      .then(setPreview).catch(() => setError('This link is not available for your account.'));
  }, [user, token]);
  if (loading) return <main className="mx-auto max-w-lg p-8">Loading…</main>;
  if (!user) return <Navigate to="/login" replace state={{ from: { pathname: location.pathname } }} />;
  const act = async (action: 'confirm' | 'dispute') => {
    setBusy(true); setError('');
    try {
      if (action === 'confirm') await api.post('/appointments/session/confirmation/confirm', { token });
      else await api.post(`/appointments/${preview?.appointmentId}/session/dispute`, { reason: 'OTHER', note: '' });
      setDone(true);
    } catch (e: any) { setError(e?.message || 'Could not update this session.'); }
    finally { setBusy(false); }
  };
  return <main className="mx-auto my-10 max-w-lg rounded-2xl border bg-white p-6 shadow-sm">
    <h1 className="text-xl font-bold">Confirm your session</h1>
    {preview && <p className="mt-3 text-sm text-slate-700">Session with {preview.providerName} on {preview.sessionDate}.</p>}
    {done ? <p className="mt-4 rounded-lg bg-emerald-50 p-3 text-emerald-800">{preview ? 'Your response was recorded.' : 'Thank you. The session was confirmed.'}</p> : preview && <div className="mt-5 flex gap-3">
      <button disabled={busy} onClick={() => void act('confirm')} className="rounded-xl bg-emerald-700 px-4 py-3 font-bold text-white">Confirm</button>
      <button disabled={busy} onClick={() => void act('dispute')} className="rounded-xl border border-red-200 px-4 py-3 font-bold text-red-700">Report a problem</button>
    </div>}
    {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
    <p className="mt-5 text-xs text-slate-500">Confirm only if the session took place. No payment is confirmed here.</p>
  </main>;
}
